import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ElementTypes, type MultiPageVideoModel } from '@bynder-studio/render-core';
import { Divider, Inspector, List, Tabs, Tooltip } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useEditor from '~/hooks/useEditor';
import { ExportSettings } from 'packages/pages/editor/RightSideMenu/Globals/exportSettings/ExportSettings';
import { ExportFormats } from 'packages/pages/editor/RightSideMenu/Globals/exportFormats/ExportFormats';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from 'packages/configs/features';
import { InspectorContent, InspectorHeader, Section } from '../index.styled';
import PosterFrameInput from './PosterFrameInput';
import Transcript, { TranscriptListItem } from '../../../components/Transcript';
import DurationInput from './DurationInput';
import BackgroundColorInput from './BackgroundColorInput';
import SizeSelector from './SizeSelector';
import GlobalAudio from './GlobalAudio';
import RequestApproval from './RequestApproval';
import ContentPropertySettings from './ContentPropertySettings';
import TextStyleSettings from './TextStyleSettings';
import { currentEditorType } from 'packages/store/creativeEditor/creativeEditor.selectors';
import useEditorSidebarShortcuts from 'packages/hooks/shortcuts/useEditorSidebarShortcuts';
import { parseShortcutForTooltip } from 'packages/common/WithShortcutLabel';
import useVariations from 'packages/pages/design/hooks/useVariations';

const Globals = () => {
    const { creativeModel, creativeType, frameRate, globalsTab, setGlobalsTab } = useEditor();
    const { currentVariation, updateVariationGlobalProperty } = useVariations();
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const isVideoCreative = creativeType === 'VIDEO';
    const isExportSettingsDesignEnabled = isPlatformAllowed([features.EXPORT_SETTINGS_DESIGN]);
    const isApprovalsEnabled = isPlatformAllowed([features.APPROVALS_ENABLED]);

    const editorType = useSelector(currentEditorType);

    useEditorSidebarShortcuts({
        setDesignTab: () => setGlobalsTab('Design'),
    });

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        const currentModel = creativeModel.getCurrentModel?.();

        if (!currentModel?.eventEmitter || !currentVariation) {
            return;
        }

        const handleTranscriptUpdated = ({ property, newValues }) => {
            if (!property?.id) {
                return;
            }

            updateVariationGlobalProperty(ElementTypes.TRANSCRIPT, property.id, newValues);
        };

        currentModel.eventEmitter.on('transcriptUpdated', handleTranscriptUpdated);

        return () => {
            currentModel.eventEmitter.off('transcriptUpdated', handleTranscriptUpdated);
        };
    }, [creativeModel, currentVariation, updateVariationGlobalProperty]);

    if (!creativeModel) {
        return null;
    }

    const isInCreate = editorType === 'create';

    const getPageTab = () => (
        <>
            {!isInCreate && (
                <>
                    <Section aria-labelledby="global-size-selector">
                        <SizeSelector
                            creativeModel={creativeModel}
                            creativeType={creativeType}
                            id="global-size-selector"
                        />
                    </Section>
                    <Divider />
                </>
            )}
            <Section aria-labelledby="global-background-color">
                <BackgroundColorInput creativeModel={creativeModel} disabled={false} />
            </Section>
            {isVideoCreative && (
                <>
                    {!isInCreate && (
                        <>
                            <Divider />
                            <Section aria-labelledby="global-video-duration">
                                <DurationInput
                                    creativeModel={creativeModel}
                                    frameRate={frameRate}
                                    id="global-video-duration"
                                    disabled={false}
                                />
                            </Section>
                            <Divider />
                        </>
                    )}
                    <Section aria-labelledby="global-video-frame-input">
                        <PosterFrameInput
                            creativeModel={creativeModel}
                            frameRate={frameRate}
                            id="global-video-frame-input"
                            disabled={false}
                        />
                    </Section>
                    {isPlatformAllowed([features.TRANSCRIPT]) && isVideoCreative && (
                        <>
                            <Divider />
                            <Section aria-labelledby="global-transcript">
                                <List>
                                    <TranscriptListItem
                                        creativeModel={creativeModel as MultiPageVideoModel}
                                        onClick={() => setGlobalsTab('Transcript')}
                                    />
                                </List>
                            </Section>
                        </>
                    )}
                    {!isPlatformAllowed([features.NEW_AUDIO_ELEMENT]) && (
                        <>
                            <Divider />
                            <Section aria-labelledby="global-audio-track-1">
                                <GlobalAudio
                                    creativeModel={creativeModel as MultiPageVideoModel}
                                    frameRate={frameRate}
                                    disabled={false}
                                    track={1}
                                />
                            </Section>
                            <Divider />
                            <Section aria-labelledby="global-audio-track-2">
                                <GlobalAudio
                                    creativeModel={creativeModel as MultiPageVideoModel}
                                    frameRate={frameRate}
                                    disabled={false}
                                    track={2}
                                />
                            </Section>
                        </>
                    )}
                </>
            )}
        </>
    );

    const getDesignTab = () => (
        <>
            {isPlatformAllowed([features.CONTENT_PROPERTIES]) && (
                <>
                    <Section aria-labelledby="design-content-properties">
                        <ContentPropertySettings creativeModel={creativeModel} />
                    </Section>
                    <Divider />
                </>
            )}
            <Section aria-labelledby="design-text-styles">
                <TextStyleSettings creativeModel={creativeModel} />
            </Section>
            {isApprovalsEnabled && (
                <>
                    <Divider />
                    <Section aria-labelledby="global-approvals">
                        <RequestApproval disabled={false} />
                    </Section>
                </>
            )}
            <Divider />
            <Section aria-labelledby="global-video-export">
                <ExportSettings />
                {isExportSettingsDesignEnabled && (
                    <>
                        <Inspector.Divider />
                        <ExportFormats />
                    </>
                )}
            </Section>
        </>
    );

    const getTranscriptTab = () => (
        <Section>
            <Transcript
                creativeModel={creativeModel as MultiPageVideoModel}
                onBack={() => setGlobalsTab('Page')}
                context="editor"
                variationId={currentVariation}
                pageIndex={creativeModel?.getCurrentPageIndex?.()}
            />
        </Section>
    );

    return (
        <InspectorContent>
            <InspectorHeader>
                <StyledTabs>
                    <Tabs.Tab
                        key={translate('editor.sidebar.globals.tab.page')}
                        label={translate('editor.sidebar.globals.tab.page')}
                        onClick={() => setGlobalsTab('Page')}
                        isActive={globalsTab === 'Page' || globalsTab === 'Transcript'}
                    />
                    <Tooltip
                        content={`${translate('editor.sidebar.globals.tab.design.tooltip')} (${parseShortcutForTooltip([
                            'd',
                        ])})`}
                    >
                        <Tabs.Tab
                            key={translate('editor.sidebar.globals.tab.design')}
                            label={translate('editor.sidebar.globals.tab.design')}
                            onClick={() => setGlobalsTab('Design')}
                            isActive={globalsTab === 'Design'}
                        />
                    </Tooltip>
                </StyledTabs>
            </InspectorHeader>
            {globalsTab === 'Page' && getPageTab()}
            {globalsTab === 'Design' && getDesignTab()}
            {globalsTab === 'Transcript' && getTranscriptTab()}
        </InspectorContent>
    );
};

export default Globals;

const StyledTabs = styled(Tabs)`
    display: flex;
    justify-content: space-evenly;

    & > div {
        flex: 1;
        min-width: 50%;
        & > button {
            width: 100%;
        }
    }
`;
